/**
 * Виды уведомлений (capability notifications, §3g): канон `kind` лент/шаблонов → русские ярлыки.
 * Источник набора `kind` — backend-foundation §3g (`order_submitted`…`password_reset`).
 * Переиспользуется колокольчиком (`app/notification-bell`) и информационным блоком
 * «Настройки → Уведомления» (`pages/settings`). Управление видами/расписанием — у администратора
 * Центрики (capability admin-notifications, Блок 8); здесь только отображение.
 */

/** Канон видов уведомлений (§3g): полный набор `kind` лент/шаблонов. */
export type NotificationKind =
  | 'order_submitted'
  | 'order_requested'
  | 'order_filled'
  | 'order_documents'
  | 'order_returned'
  | 'order_in_progress'
  | 'order_done'
  | 'order_cancelled'
  | 'order_restored'
  | 'order_client_cancelled'
  | 'order_client_restored'
  | 'training_change_proposed'
  | 'training_change_approved'
  | 'training_change_rejected'
  | 'training_change_client_proposed'
  | 'training_change_client_approved'
  | 'training_change_client_rejected'
  | 'training_completed'
  | 'training_reminder'
  | 'training_overdue'
  | 'eco_reminder'
  | 'eco_overdue'
  | 'account_created'
  | 'password_reset'
  | 'training_subscription_expiring'
  | 'question_created'
  | 'question_client_replied'
  | 'question_answered'

/** Канон `kind` → ярлык вида уведомления (строка каталога §3g). Запись по union —
 * пропуск/опечатка канон-`kind` ловится компилятором, не только тестом. */
export const NOTIFICATION_KIND_LABELS: Record<NotificationKind, string> = {
  order_submitted: 'Новая заявка',
  // Команде: заявка на обучение — нужны договор и счёт (training-order-filling); клиенту не приходит.
  order_requested: 'Заявка на обучение: договор и счёт',
  // Команде: клиент заполнил и подтвердил данные слушателей заказа «На заполнении» (В12).
  order_filled: 'Заказ заполнен',
  order_documents: 'В заказ добавлен документ',
  order_returned: 'Возврат на правки',
  order_in_progress: 'Заказ принят в работу',
  order_done: 'Заказ готов',
  // Отмена и восстановление заказа (order-cancel-restore): клиенту — если это сделала Центрика,
  // команде — если клиент.
  order_cancelled: 'Заказ отменён',
  order_restored: 'Заказ восстановлен',
  order_client_cancelled: 'Клиент отменил заказ',
  order_client_restored: 'Клиент восстановил заказ',
  // Изменения состава заявки на согласовании (training-change-approval): клиенту — о предложении
  // Центрики и о решении по его изменениям, команде — о предложении клиента и о его решении.
  training_change_proposed: 'Заявка изменена: требуется согласование',
  training_change_approved: 'Изменения в заявке согласованы',
  training_change_rejected: 'Изменения в заявке отклонены',
  training_change_client_proposed: 'Клиент изменил заявку: требуется согласование',
  training_change_client_approved: 'Клиент согласовал изменения в заявке',
  training_change_client_rejected: 'Клиент отклонил изменения в заявке',
  training_completed: 'Обучение по программе завершено',
  training_reminder: 'Напоминание о повторном обучении',
  training_overdue: 'Просрочка обучения',
  eco_reminder: 'Экология: приближается срок',
  eco_overdue: 'Экология: срок истёк',
  account_created: 'Приглашение в кабинет',
  password_reset: 'Сброс пароля',
  // Уведомление администраторам Центрики (training-subscription-expiry-notice); клиенту не приходит.
  training_subscription_expiring: 'Абонентское обслуживание по обучению заканчивается',
  // «Мои вопросы» (client-questions; ТЗ п.9.7): команде — новый вопрос и уточнение клиента, клиенту —
  // ответ Центрики.
  question_created: 'Новый вопрос клиента',
  question_client_replied: 'Клиент дополнил вопрос',
  question_answered: 'Центрика ответила на вопрос',
}

/**
 * Порядок видов в информационном блоке «Настройки → Уведомления» (клиентская сторона).
 * Системное письмо `password_reset` адресовано конкретному пользователю, а не компании, и в перечне
 * видов уведомлений по заказам/обучению не показывается.
 */
export const NOTIFICATION_INFO_KINDS = [
  'order_submitted',
  'order_documents',
  'order_returned',
  'order_in_progress',
  'order_done',
  'order_cancelled',
  'order_restored',
  'training_change_proposed',
  'training_change_approved',
  'training_change_rejected',
  'training_completed',
  'training_reminder',
  'training_overdue',
  'question_answered',
  'account_created',
] as const

/**
 * Письма раздела «Экология» (ecology-reminders) — только у клиента-компании: у физлица раздела нет,
 * и такие письма ему не приходят.
 */
export const NOTIFICATION_INFO_KINDS_COMPANY = ['eco_reminder', 'eco_overdue'] as const

/** Ярлык вида уведомления; для неизвестного `kind` — сам код (страховка от рассинхрона с бэкендом). */
export function notificationKindLabel(kind: string): string {
  return (NOTIFICATION_KIND_LABELS as Record<string, string>)[kind] ?? kind
}
